import { supabase } from '@/lib/supabase'
import type { AffiliateLead, ListAffiliateLeadsParams, ListAffiliateLeadsResult } from '../types'

export type CreateAffiliateLeadPayload = {
  name: string
  email: string
  whatsapp: string
  instagram?: string
  facebook?: string
  youtube?: string
  discord?: string
  otherSocials?: string
  notes?: string
  source?: string
}

function normalizeString(v: string | undefined | null): string | null {
  if (typeof v !== 'string') return null
  const trimmed = v.trim()
  return trimmed ? trimmed : null
}

export async function createAffiliateLead(payload: CreateAffiliateLeadPayload): Promise<{ id: string }> {
  const name = normalizeString(payload.name) || ''
  const email = normalizeString(payload.email) || ''
  const whatsapp = normalizeString(payload.whatsapp) || ''
  if (!name) throw new Error('Nome obrigatório')
  if (!email) throw new Error('E-mail obrigatório')
  if (!whatsapp) throw new Error('WhatsApp obrigatório')

  const body: any = {
    full_name: name,
    email,
    whatsapp,
    social_instagram: normalizeString(payload.instagram),
    social_facebook: normalizeString(payload.facebook),
    social_youtube: normalizeString(payload.youtube),
    social_discord: normalizeString(payload.discord),
    social_other: normalizeString(payload.otherSocials),
    notes: normalizeString(payload.notes),
    source: normalizeString(payload.source) || 'landing_seja_afiliado',
  }

  const { data, error } = await supabase
    .from('affiliate_leads')
    .insert(body)
    .select('id')
    .single()

  if (error) throw error
  return { id: String((data as any).id) }
}

export async function listAffiliateLeads(params: ListAffiliateLeadsParams): Promise<ListAffiliateLeadsResult> {
  const search = params.search?.trim() || ''
  const page = params.page && params.page > 0 ? params.page : 1
  const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 25
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('affiliate_leads')
    .select(
      [
        'id',
        'full_name',
        'email',
        'whatsapp',
        'social_instagram',
        'social_facebook',
        'social_youtube',
        'social_discord',
        'social_other',
        'notes',
        'source',
        'created_at',
      ].join(','),
      { count: 'exact' },
    )

  if (search) {
    const pattern = `%${search}%`
    query = query.or(
      [
        `full_name.ilike.${pattern}`,
        `email.ilike.${pattern}`,
        `whatsapp.ilike.${pattern}`,
        `social_instagram.ilike.${pattern}`,
        `social_facebook.ilike.${pattern}`,
        `social_youtube.ilike.${pattern}`,
        `social_discord.ilike.${pattern}`,
        `social_other.ilike.${pattern}`,
      ].join(','),
    )
  }

  query = query.order('created_at', { ascending: false }).range(from, to)

  const { data, error, count } = await query
  if (error) throw error

  const items: AffiliateLead[] = (data ?? []).map((row: any) => ({
    id: String(row.id),
    full_name: row.full_name ?? '',
    email: row.email ?? '',
    whatsapp: row.whatsapp ?? null,
    social_instagram: row.social_instagram ?? null,
    social_facebook: row.social_facebook ?? null,
    social_youtube: row.social_youtube ?? null,
    social_discord: row.social_discord ?? null,
    social_other: row.social_other ?? null,
    notes: row.notes ?? null,
    source: row.source ?? 'landing_seja_afiliado',
    created_at: row.created_at ?? null,
  }))

  return {
    items,
    total: count ?? items.length,
  }
}

export type UpdateAffiliateLeadPayload = Partial<{
  full_name: string
  email: string
  whatsapp: string
  social_instagram: string | null
  social_facebook: string | null
  social_youtube: string | null
  social_discord: string | null
  social_other: string | null
  notes: string | null
}>

export async function updateAffiliateLead(id: string, patch: UpdateAffiliateLeadPayload): Promise<void> {
  const body: Record<string, string | null> = {}

  if (Object.prototype.hasOwnProperty.call(patch, 'full_name')) {
    body.full_name = normalizeString(patch.full_name ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'email')) {
    body.email = normalizeString(patch.email ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'whatsapp')) {
    body.whatsapp = normalizeString(patch.whatsapp ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'social_instagram')) {
    body.social_instagram = normalizeString(patch.social_instagram ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'social_facebook')) {
    body.social_facebook = normalizeString(patch.social_facebook ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'social_youtube')) {
    body.social_youtube = normalizeString(patch.social_youtube ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'social_discord')) {
    body.social_discord = normalizeString(patch.social_discord ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'social_other')) {
    body.social_other = normalizeString(patch.social_other ?? null)
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'notes')) {
    body.notes = normalizeString(patch.notes ?? null)
  }

  const keys = Object.keys(body)
  if (keys.length === 0) return

  const { error } = await supabase
    .from('affiliate_leads')
    .update(body)
    .eq('id', id)

  if (error) throw error
}

export async function deleteAffiliateLead(id: string): Promise<void> {
  const { error } = await supabase
    .from('affiliate_leads')
    .delete()
    .eq('id', id)

  if (error) throw error
}
